'use client'

import { useState, useContext, useEffect, useCallback } from 'react'
import {
  Table,
  TableHeader,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@/components/ui/shadcn/table'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/shadcn/button'
import { Badge } from '@/components/ui/shadcn/badge'
import {
  Pencil as EditIcon,
  Eye as ViewIcon,
  Trash2 as DeleteIcon,
} from 'lucide-react'
import CreateUserGroupModal from '@/components/common/modals/CreateUserGroupModal'
import { AuthContext } from '@/contexts/AuthContext'
import { fetcher } from '@/lib/api/fetch'
import ErrorAlerts from '@/components/common/feedback/ErrorAlerts'
import ConfirmDelete from '@/components/common/modals/ConfirmDelete'
import { SettingsContext } from '@/contexts/SettingsContext'
import UserGroupChip from '@/components/common/UserGroupChip'

export default function Groups() {
  const [showCreate, setShowCreate] = useState(false)
  const [showDelete, setShowDelete] = useState(false)
  const [toEdit, setToEdit] = useState(null)
  const [toDelete, setToDelete] = useState(null)
  const [initialLoad, setInitialLoad] = useState(true)
  const [loading, setLoading] = useState(false)
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState(null)
  const [error, setError] = useState(null)
  const authContext = useContext(AuthContext)
  const settingsContext = useContext(SettingsContext)

  const getUserGroups = useCallback(async () => {
    setLoading(true)

    const res = await fetcher(
      `/api/admin/organization/${authContext.user.organization.id}/usergroups`,
      { method: 'GET' },
      authContext,
    )

    if (res.status === 401) {
      setLoading(false)
      return
    }

    if (res.status !== 200) {
      console.error('Failed to get organizations for user')
      setLoading(false)
      return
    }

    const data = await res.json()
    settingsContext.setUserGroups(data.userGroups || [])
    setLoading(false)
  }, [authContext, settingsContext])

  const submitUserGroup = async (e, edit) => {
    e.preventDefault()
    setCreating(true)

    let path = '/api/admin/usergroup'
    if (edit?.id) path += `/${edit.id}`

    const res = await fetcher(
      path,
      {
        method: 'POST',
        body: JSON.stringify({
          userGroup: {
            name: e.target.name.value,
            organization: authContext.user.organization.id,
            roles: JSON.parse(e.target.roles.value),
            color: e.target.color?.value || '#1976d2',
          },
        }),
      },
      authContext,
    )

    const data = await res.json()
    if (res.status !== 200) {
      setCreateError(data.message)
      return
    }

    if (edit?.id) {
      settingsContext.updateUserGroup(data.userGroup.id, data.userGroup)
    } else {
      settingsContext.addUserGroup(data.userGroup)
    }
    setCreating(false)
    setCreateError(null)
    setShowCreate(false)
    setToEdit(null)
  }

  const deleteUserGroup = async id => {
    settingsContext.removeUserGroup(id)
    setToDelete(null)
    setError(null)
  }

  const getRole = useCallback(
    id => {
      const role = settingsContext.roles.find(r => r.id === id)
      return role?.name || null
    },
    [settingsContext.roles],
  )

  useEffect(() => {
    if (initialLoad) {
      getUserGroups()
      setInitialLoad(false)
    }
  }, [initialLoad, getUserGroups])

  // Listen to changes in roles so that we can remove
  // roles from a user group if they are removed from
  // the system.
  useEffect(() => {
    // Loop through all user groups and check the roles
    // still exist
    const newGroups = []
    for (const group of settingsContext.userGroups) {
      const newGroup = { ...group }
      newGroup.roles = newGroup.roles.filter(role =>
        settingsContext.roles.some(r => r.id === role),
      )
      newGroups.push(newGroup)
    }
    // Check if a change has been made
    if (
      JSON.stringify(newGroups) === JSON.stringify(settingsContext.userGroups)
    )
      return
    settingsContext.setUserGroups(newGroups)
  }, [settingsContext])

  if (!authContext.user?.isAuthorized('viewUserGroups')) {
    return null
  }

  return (
    <div className="rounded-lg border bg-card p-4">
      {loading && (
        <div className="flex justify-center py-6">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      )}
      {!loading && (
        <div className="grid gap-4">
          <div className="flex items-center">
            <h3 className="text-lg font-semibold flex-1">Groups</h3>
            <ErrorAlerts error={error} />
            <Button
              onClick={() => setShowCreate(true)}
              disabled={!authContext.user?.isAuthorized('createUserGroups')}
            >
              Create Group
            </Button>
          </div>
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[30%]">Name</TableHead>
                  <TableHead>Roles</TableHead>
                  <TableHead className="w-[15%] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!settingsContext.userGroups.length && (
                  <TableRow>
                    <TableCell colSpan={3}>
                      No user groups have been created yet. Click{' '}
                      <b>Create Group</b> above to create one.
                    </TableCell>
                  </TableRow>
                )}
                {!!settingsContext.userGroups.length &&
                  settingsContext.userGroups
                    .sort((a, b) => a.name - b.name)
                    .map(ug => (
                      <TableRow key={ug.id}>
                        <TableCell>
                          <UserGroupChip userGroup={ug} variant="filled" />
                        </TableCell>
                        <TableCell className="whitespace-normal">
                          {!ug.roles?.length && (
                            <Badge variant="outline">No Roles</Badge>
                          )}
                          {ug.roles?.map(role => (
                            <Badge key={role} className="mr-1">
                              {getRole(role)}
                            </Badge>
                          ))}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={
                              authContext.user?.isAuthorized('editUserGroups')
                                ? 'Edit group'
                                : 'View group'
                            }
                            onClick={() => {
                              setToEdit(ug)
                              setShowCreate(true)
                            }}
                          >
                            {authContext.user?.isAuthorized(
                              'editUserGroups',
                            ) ? (
                              <EditIcon className="size-4" />
                            ) : (
                              <ViewIcon className="size-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label="Delete group"
                            onClick={() => {
                              setToDelete(ug)
                              setShowDelete(true)
                            }}
                            disabled={
                              !authContext.user?.isAuthorized(
                                'deleteUserGroups',
                              )
                            }
                            className="text-destructive hover:text-destructive"
                          >
                            <DeleteIcon className="size-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
      <CreateUserGroupModal
        open={showCreate}
        edit={toEdit}
        onClose={() => {
          setShowCreate(false)
          setToEdit(null)
        }}
        submit={submitUserGroup}
        loading={creating}
        error={createError}
      />
      <ConfirmDelete
        open={showDelete}
        setOpen={() => setShowDelete(false)}
        title="Delete User Group"
        description={
          <>
            <div>
              <p>
                Removing a user group that has been assigned to users will
                result in those users having the user group removed from their
                profile. This will mean they have no access unless they are an
                owner.
              </p>
            </div>
            <div>
              <p>
                To confirm the removal of this user group type its name:{' '}
                <b>{toDelete?.name}</b>
              </p>
            </div>
          </>
        }
        buttonText="Remove User Group"
        fieldLabel="User Group Name"
        fieldType="text"
        reference={toDelete?.name}
        apiPath={`/api/admin/usergroup/${toDelete?.id}`}
        onComplete={() => deleteUserGroup(toDelete?.id)}
        onCancel={() => setToDelete(null)}
      />
    </div>
  )
}
